import { Modal, Radio, Select, Button, Form, Typography, Divider } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useMainContext } from '../../hooks/useMainContext';
import { DoorType, OpeningType, AttachmentType, DoorSwing } from '../../config';
import './DoorForm.css';
import Draggable from 'react-draggable';
import { TbX, TbArrowsMove } from 'react-icons/tb';
import RectangleButton from '../Globle/RectangleButton';
import {
    DoorwayIcon,
    DoubleDoorIcon,
    SingleDoorIcon,
    SlidingDoorIcon,
} from '../Globle/Icon';

export const DoorForm = observer(() => {
    const doorTypeOptions = [
        {
            value: DoorType.SINGLE,
            label: 'Single',
            icon: <SingleDoorIcon />,
        },
        {
            value: DoorType.DOUBLE,
            label: 'Double',
            icon: <DoubleDoorIcon />,
        },
        {
            value: DoorType.PATIO,
            label: 'Patio',
            icon: <SlidingDoorIcon />,
        },
        {
            value: DoorType.DOORWAY,
            label: 'Doorway',
            icon: <DoorwayIcon />,
        },
    ];
    const { closetManager, drawingManager } = useMainContext();
    const { control, handleSubmit, watch, reset, setValue } = useForm({
        defaultValues: {
            closetType: AttachmentType.DOOR,
            doorType: DoorType.SINGLE,
            doorOpening: OpeningType.INSIDE,
            doorSwing: DoorSwing.LEFT,
        },
    });
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (closetManager.shouldDoorFormOpen()) {
            setModalPosition({ x: 0, y: 0 });
        }
    }, [closetManager.shouldDoorFormOpen()]);
    useEffect(() => {
        const selectedAttachment = closetManager.selectedAttachment;
        if (!selectedAttachment) return;
        setValue('closetType', selectedAttachment.attachmentType);
        setValue('doorType', selectedAttachment.type);
        setValue('doorOpening', selectedAttachment.opening);
        setValue('doorSwing', selectedAttachment.swing);
    }, [closetManager.selectedAttachment, closetManager.attachmentEditMode]);
    const closetType = watch('closetType');
    const doorType = watch('doorType');

    const onSubmit = (data) => {
        if (!closetManager.attachmentEditMode) {
            closetManager.setAttachmentAttributes(
                data.closetType,
                data.doorType,
                data.doorOpening,
                data.doorSwing,
            );
            drawingManager.setDrawingForm(false);
            closetManager.setSelectedCloset(null);
            closetManager.drawNewClosets();
        } else {
            closetManager.updateAttachmentAttributes(
                data.closetType,
                data.doorType,
                data.doorOpening,
                data.doorSwing,
            );
            drawingManager.setDrawingForm(false);
            closetManager.setAttachmentEditMode(false);
            closetManager.drawNewClosets();
        }
        closetManager.setSelectedAttachment(null);
        reset();
    };

    const closeModal = () => {
        if (!closetManager.attachmentEditMode) {
            closetManager.setAttachmentAttributes(
                AttachmentType.DOOR,
                DoorType.SINGLE,
                OpeningType.INSIDE,
                DoorSwing.LEFT,
            );
            drawingManager.setDrawingForm(false);
            closetManager.setSelectedCloset(null);
            closetManager.drawNewClosets();
        }
        drawingManager.setDoorMode(false);
        drawingManager.setDrawingForm(false);
        closetManager.setAttachmentEditMode(false);
        closetManager.setSelectedAttachment(null);
        reset();
    };

    return (
        <Modal
            className="door-form-modal"
            width={330}
            centered
            styles={{
                content: {
                    padding: '16px',
                    borderRadius: '20px',
                },
            }}
            modalRender={(modal) => (
                <Draggable
                    handle=".drag-button"
                    cancel=".ant-input, .ant-btn:not(.drag-button)"
                    position={modalPosition}
                    onStop={(_, data) => {
                        setModalPosition({ x: data.x, y: data.y });
                    }}>
                    <div>{modal}</div>
                </Draggable>
            )}
            onCancel={closeModal}
            footer={null}
            closable={false}
            open={closetManager.shouldDoorFormOpen()}>
            <div className="door-form-wrapper">
                <div
                    className="border-b-[1px] border-theme-border pb-2"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10,
                    }}>
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        Door / Window
                    </Typography.Title>
                    <div style={{ display: 'flex' }}>
                        <Button
                            className="drag-button flex items-center justify-center p-0 w-[32px] h-[32px]"
                            style={{ cursor: 'move', border: 'none' }}>
                            <TbArrowsMove size={20} />{' '}
                        </Button>
                        <Button
                            type="text"
                            className="flex items-center justify-center p-0 w-[32px] h-[32px]"
                            onClick={closeModal}>
                            <TbX size={20} />
                        </Button>
                    </div>
                </div>
                <Form
                    layout="vertical"
                    onFinish={handleSubmit(onSubmit)}
                    className="door-form !font-favorit">
                    <Form.Item
                        label="Closet Type :"
                        colon={false}
                        rules={[
                            {
                                required: true,
                                message: 'Please select a closet type',
                            },
                        ]}>
                        <Controller
                            name="closetType"
                            control={control}
                            render={({ field }) => (
                                <Radio.Group {...field}>
                                    {Object.entries(AttachmentType).map(
                                        ([key, value]) => (
                                            <Radio key={value} value={value}>
                                                {key}
                                            </Radio>
                                        ),
                                    )}
                                </Radio.Group>
                            )}
                        />
                    </Form.Item>

                    {closetType === AttachmentType.DOOR && (
                        <>
                            <Divider className="!my-[12px]  !border-theme-border" />

                            <Form.Item
                                colon={false}
                                label="Type :"
                                className="door-type"
                                // layout="vertical"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select a door type',
                                    },
                                ]}>
                                <Controller
                                    name="doorType"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="grid grid-cols-3 gap-2">
                                            {doorTypeOptions.map(
                                                (button, index) => (
                                                    <RectangleButton
                                                        key={index}
                                                        Icon={button.icon}
                                                        label={button.label}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            field.onChange(
                                                                button.value,
                                                            );
                                                        }}
                                                        isActive={
                                                            field.value ===
                                                            button.value
                                                        }
                                                        className={'h-[80px]'}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    )}
                                />
                            </Form.Item>
                        </>
                    )}
                    <Divider className="!my-[12px]  !border-theme-border" />

                    <h1 className="font-favorit text-[14px] font-[700] leading-[21px] mb-[12px]">
                        Opening
                    </h1>

                    {closetType === AttachmentType.DOOR &&
                        doorType === DoorType.SINGLE && (
                            <Form.Item
                                colon={false}
                                label="Swing :"
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Please select door swing direction',
                                    },
                                ]}>
                                <Controller
                                    name="doorSwing"
                                    control={control}
                                    render={({ field }) => (
                                        <Radio.Group {...field}>
                                            <Radio value={DoorSwing.LEFT}>
                                                Left
                                            </Radio>
                                            <Radio value={DoorSwing.RIGHT}>
                                                Right
                                            </Radio>
                                        </Radio.Group>
                                    )}
                                />
                            </Form.Item>
                        )}

                    <Form.Item
                        label="Direction :"
                        colon={false}
                        rules={[
                            {
                                required: true,
                                message: 'Please select an opening direction',
                            },
                        ]}>
                        <Controller
                            name="doorOpening"
                            control={control}
                            render={({ field }) => (
                                <Radio.Group {...field}>
                                    {Object.values(OpeningType).map((type) => (
                                        <Radio key={type} value={type}>
                                            {type}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            )}
                        />
                    </Form.Item>

                    <div className="form-actions">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="bg-theme-orange text-theme-white !!hover:bg-theme-orange-hover !shadow-none save-changes-button">
                            OK
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
});
